import { useEffect, useState, useCallback } from "react";
import api, { fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { usePageContent } from "@/hooks/usePageContent";
import { CoachTip } from "@/components/HelpHint";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { GraduationCap, Award, Clock, CheckCircle2, AlertTriangle, BookOpen, Video, Users, Sparkles } from "lucide-react";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);
const inDays = (n) => new Date(Date.now() + n * 86400000).toISOString().slice(0, 10);
const expired = (d) => d && d < today();
const soon = (d) => d && d >= today() && d <= inDays(60);

const FORMAT_ICON = { "e-learning": BookOpen, workshop: Users, webinar: Video };
const CAT_TONE = {
  Compliance: "bg-rose-50 text-rose-700 border-rose-200",
  Technical: "bg-sky-50 text-sky-700 border-sky-200",
  Leadership: "bg-violet-50 text-violet-700 border-violet-200",
  Wellbeing: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Professional: "bg-amber-50 text-amber-700 border-amber-200",
};

function StatusPill({ record }) {
  if (expired(record.expiry_date)) return <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-rose-100 text-rose-700 rounded-full px-2 py-0.5"><AlertTriangle size={11} />Renewal overdue</span>;
  if (soon(record.expiry_date)) return <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-amber-100 text-amber-700 rounded-full px-2 py-0.5"><Clock size={11} />Renewal due</span>;
  if (record.status === "completed") return <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5"><CheckCircle2 size={11} />Completed</span>;
  return <span className="inline-flex items-center gap-1 text-[11px] font-semibold bg-sky-100 text-sky-700 rounded-full px-2 py-0.5">{record.status || "In progress"}</span>;
}

function RecordCard({ r }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 hover:shadow-md transition-shadow" data-testid={`learning-record-${r.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className="rounded-lg bg-[#EFF8FF] p-2 text-[#2EA3FD] shrink-0"><Award size={18} /></span>
          <div className="min-w-0">
            <p className="font-semibold text-[#0B1A2F] truncate">{r.course}</p>
            <p className="text-xs text-slate-400">{r.provider || "CASS"}</p>
          </div>
        </div>
        <StatusPill record={r} />
      </div>
      <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
        {r.completed_date && <span>Completed <strong className="text-[#0B1A2F]">{fmtDate(r.completed_date)}</strong></span>}
        {r.expiry_date && <span>Expires <strong className={expired(r.expiry_date) ? "text-rose-600" : "text-[#0B1A2F]"}>{fmtDate(r.expiry_date)}</strong></span>}
        {r.cpd_hours ? <span>CPD <strong className="text-[#0B1A2F]">{r.cpd_hours}h</strong></span> : null}
      </div>
    </div>
  );
}

export default function Learning() {
  const { user } = useAuth();
  const pc = usePageContent();
  const lh = pc?.learning?.hero || {};
  const empId = user?.employee_id;
  const [records, setRecords] = useState([]);
  const [courses, setCourses] = useState([]);

  const load = useCallback(() => {
    if (empId) api.get(`/employees/${empId}/training`).then((r) => setRecords(r.data)).catch(() => setRecords([]));
    api.get("/learning/catalogue").then((r) => setCourses(r.data.courses || [])).catch(() => setCourses([]));
  }, [empId]);
  useEffect(() => { load(); }, [load]);

  const current = records.filter((r) => !r.completed_date || !r.expiry_date || r.expiry_date >= today() || expired(r.expiry_date));

  const register = async (c) => {
    try { await api.post(`/learning/catalogue/${c.id}/register`); toast.success(`Registered interest in "${c.title}" — HR will be in touch.`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="learning-page">
      <CoachTip tipKey="learning" title="Keep your training on track">Browse Available courses and Register interest — HR will help arrange enrolment. Renewal-due items are flagged in My Learning.</CoachTip>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#0B1A2F] via-[#0F2C50] to-[#36776B] text-white p-6 sm:p-8" data-testid="learning-hero">
        <div className="absolute -right-14 -top-14 h-52 w-52 rounded-full bg-white/10" />
        <div className="relative">
          <p className="text-xs uppercase tracking-widest text-[#8FD0FF] flex items-center gap-2"><Sparkles size={14} />{lh.eyebrow || "Learning & Development"}</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold font-display mt-1">{lh.title || "Grow your skills at CASS"}</h1>
          <p className="text-slate-200 mt-2 max-w-lg">{lh.subtitle || "Track your training, keep certifications up to date, and explore new courses to build your career."}</p>
          <div className="flex flex-wrap gap-3 mt-5">
            <div className="bg-white/10 rounded-lg px-4 py-2"><p className="text-[11px] text-[#B9DCFF]">My courses</p><p className="text-xl font-bold" data-testid="learning-stat-total">{records.length}</p></div>
            <div className="bg-white/10 rounded-lg px-4 py-2"><p className="text-[11px] text-[#B9DCFF]">Available</p><p className="text-xl font-bold" data-testid="learning-stat-available">{courses.length}</p></div>
            <div className="bg-white/10 rounded-lg px-4 py-2"><p className="text-[11px] text-[#B9DCFF]">Renewals due</p><p className="text-xl font-bold" data-testid="learning-stat-due">{current.filter((r) => soon(r.expiry_date) || expired(r.expiry_date)).length}</p></div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="my" className="space-y-4">
        <TabsList data-testid="learning-tabs">
          <TabsTrigger value="my" data-testid="learning-tab-my">My Learning</TabsTrigger>
          <TabsTrigger value="available" data-testid="learning-tab-available">Available</TabsTrigger>
          <TabsTrigger value="history" data-testid="learning-tab-history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="my" className="space-y-4" data-testid="learning-my-panel">
          {current.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-10 text-center text-slate-400" data-testid="learning-my-empty">
              <GraduationCap size={30} className="mx-auto mb-2 text-slate-300" />
              <p>You have no active courses yet. Browse the <strong>Available</strong> tab to get started.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {current.map((r) => <RecordCard key={r.id} r={r} />)}
            </div>
          )}
        </TabsContent>

        <TabsContent value="available" data-testid="learning-available-panel">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {courses.map((c) => {
              const Icon = FORMAT_ICON[c.format] || BookOpen;
              return (
                <div key={c.id} className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 flex flex-col hover:shadow-md transition-shadow" data-testid={`learning-course-${c.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-flex rounded-md border px-2 py-0.5 text-[11px] font-medium ${CAT_TONE[c.category] || "bg-slate-100 text-slate-600 border-slate-200"}`}>{c.category || "General"}</span>
                    {c.mandatory && <span className="text-[11px] font-semibold bg-rose-50 text-rose-600 border border-rose-200 rounded-md px-2 py-0.5">Mandatory</span>}
                  </div>
                  <h3 className="font-semibold text-[#0B1A2F] mt-3 flex items-start gap-2"><Icon size={17} className="text-[#2EA3FD] mt-0.5 shrink-0" />{c.title}</h3>
                  <p className="text-sm text-slate-500 mt-1.5 flex-1">{c.description}</p>
                  <div className="flex items-center gap-3 text-xs text-slate-400 mt-3">
                    <span className="capitalize">{c.format}</span>{c.duration && <span>· {c.duration}</span>}{c.provider && <span>· {c.provider}</span>}
                  </div>
                  <Button size="sm" disabled={c.registered} onClick={() => register(c)} data-testid={`learning-register-${c.id}`}
                    className={`mt-4 ${c.registered ? "bg-emerald-500 hover:bg-emerald-500" : "bg-[#2EA3FD] hover:bg-[#1B8EE6]"}`}>
                    {c.registered ? <><CheckCircle2 size={15} className="mr-1.5" />Interest registered</> : "Register interest"}
                  </Button>
                </div>
              );
            })}
            {courses.length === 0 && <p className="text-sm text-slate-400 col-span-full text-center py-10">No courses available yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="history" data-testid="learning-history-panel">
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-600">
                <th className="px-5 py-3">Course</th><th className="px-5 py-3">Provider</th><th className="px-5 py-3">Completed</th><th className="px-5 py-3">Expires</th><th className="px-5 py-3">Status</th>
              </tr></thead>
              <tbody className="divide-y divide-slate-50">
                {records.length === 0 ? <tr><td colSpan={5} className="text-center text-slate-400 py-10">No training history recorded yet.</td></tr> :
                  records.map((r) => (
                    <tr key={r.id} data-testid={`learning-history-row-${r.id}`}>
                      <td className="px-5 py-3 font-medium text-[#0B1A2F]">{r.course}</td>
                      <td className="px-5 py-3 text-slate-600">{r.provider || "—"}</td>
                      <td className="px-5 py-3">{r.completed_date ? fmtDate(r.completed_date) : "—"}</td>
                      <td className="px-5 py-3">{r.expiry_date ? fmtDate(r.expiry_date) : "—"}</td>
                      <td className="px-5 py-3"><StatusPill record={r} /></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
